/**
 * scrollbar.js — Custom scrollbar logic
 *
 * Pure controller. Takes the scrollable content element and the
 * already-rendered scrollbar elements (track, thumb, buttons) and
 * wires up:
 *   - Drag thumb to scroll proportionally
 *   - Hold up/down buttons → continuous scroll (16ms tick)
 *   - Click top/bottom buttons → smooth jump
 *   - Auto-resize thumb on scroll, content change, resize
 *   - Auto-hide bar when content fits
 *
 * Logic ported from old RedConcierge/static/javascript/services/
 * ws-custom-scrollbar.js. Behavior preserved.
 *
 * USAGE:
 *   const destroy = attachScrollbar({
 *     content, wrapper, scrollbar, track, thumb,
 *     btnUp, btnDown, btnTop, btnBottom
 *   });
 *
 *   destroy();   // remove all listeners + observers
 */

const STEP_PX = 6;
const STEP_INTERVAL_MS = 16;
const THUMB_MIN_PX = 50;
const FIT_BUFFER_PX = 5;

export function attachScrollbar({
  content,
  wrapper,
  scrollbar,
  track,
  thumb,
  btnUp,
  btnDown,
  btnTop,
  btnBottom,
}) {
  if (!content || !track || !thumb) {
    console.warn("[RedConcierge] attachScrollbar: missing required elements");
    return () => {};
  }

  let scrollInterval = null;
  let isDragging = false;
  let startY = 0;
  let startTop = 0;
  let thumbScheduled = false;

  const updateThumb = () => {
    const scrollHeight = content.scrollHeight;
    const clientHeight = content.clientHeight;
    const overflow = scrollHeight > clientHeight + FIT_BUFFER_PX;

    if (scrollbar) scrollbar.style.display = overflow ? "" : "none";
    if (wrapper) wrapper.classList.toggle("ws-scroll-overflow", overflow);
    if (!overflow) return;

    const trackHeight = track.clientHeight;
    if (trackHeight <= 0) return;

    const visibleRatio = clientHeight / scrollHeight;
    const thumbHeight = Math.max(trackHeight * visibleRatio, THUMB_MIN_PX);
    thumb.style.height = thumbHeight + "px";

    const maxScroll = scrollHeight - clientHeight;
    const scrollPercent = maxScroll > 0 ? content.scrollTop / maxScroll : 0;
    const thumbMax = trackHeight - thumbHeight;
    thumb.style.top = scrollPercent * thumbMax + "px";
  };

  const scheduleUpdateThumb = () => {
    if (thumbScheduled) return;
    thumbScheduled = true;
    requestAnimationFrame(() => {
      updateThumb();
      thumbScheduled = false;
    });
  };

  // ─── Drag (mouse) ────────────────────────────────────
  const onThumbMouseDown = (e) => {
    isDragging = true;
    startY = e.clientY;
    startTop = parseFloat(thumb.style.top) || 0;
    document.body.style.userSelect = "none";
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    const deltaY = e.clientY - startY;
    const newTop = startTop + deltaY;
    const maxThumbTop = track.clientHeight - thumb.clientHeight;
    const clampedTop = Math.max(0, Math.min(newTop, maxThumbTop));
    thumb.style.top = clampedTop + "px";

    const scrollPercent = maxThumbTop > 0 ? clampedTop / maxThumbTop : 0;
    const maxScroll = content.scrollHeight - content.clientHeight;
    content.scrollTop = scrollPercent * maxScroll;
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    isDragging = false;
    document.body.style.userSelect = "auto";
  };

  // ─── Drag (touch) ────────────────────────────────────
  const onThumbTouchStart = (e) => {
    isDragging = true;
    startY = e.touches[0].clientY;
    startTop = parseFloat(thumb.style.top) || 0;
  };

  const onTouchMove = (e) => {
    if (!isDragging) return;
    const deltaY = e.touches[0].clientY - startY;
    const newTop = startTop + deltaY;
    const maxThumbTop = track.clientHeight - thumb.clientHeight;
    const clampedTop = Math.max(0, Math.min(newTop, maxThumbTop));
    thumb.style.top = clampedTop + "px";

    const scrollPercent = maxThumbTop > 0 ? clampedTop / maxThumbTop : 0;
    const maxScroll = content.scrollHeight - content.clientHeight;
    content.scrollTop = scrollPercent * maxScroll;
  };

  const onTouchEnd = () => {
    isDragging = false;
  };

  // ─── Hold-to-scroll ──────────────────────────────────
  const startScrolling = (amount) => {
    if (scrollInterval) return;
    scrollInterval = setInterval(() => {
      content.scrollBy({ top: amount, behavior: "smooth" });
    }, STEP_INTERVAL_MS);
  };

  const stopScrolling = () => {
    if (scrollInterval) clearInterval(scrollInterval);
    scrollInterval = null;
  };

  const upDown = (btn, dir) => {
    if (!btn) return [];
    const handlers = [
      ["mousedown", () => startScrolling(dir * STEP_PX)],
      ["mouseup", stopScrolling],
      ["mouseleave", stopScrolling],
      [
        "touchstart",
        (e) => {
          e.preventDefault();
          startScrolling(dir * STEP_PX);
        },
      ],
      ["touchend", stopScrolling],
    ];
    handlers.forEach(([ev, fn]) => btn.addEventListener(ev, fn));
    return handlers.map(([ev, fn]) => () => btn.removeEventListener(ev, fn));
  };

  // ─── Jump buttons ────────────────────────────────────
  const onTopClick = () => {
    content.scrollTo({ top: 0, behavior: "smooth" });
  };
  const onBottomClick = () => {
    content.scrollTo({ top: content.scrollHeight, behavior: "smooth" });
  };

  // ─── Wire up ─────────────────────────────────────────
  thumb.addEventListener("mousedown", onThumbMouseDown);
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);

  thumb.addEventListener("touchstart", onThumbTouchStart);
  document.addEventListener("touchmove", onTouchMove);
  document.addEventListener("touchend", onTouchEnd);

  const btnUpCleanup = upDown(btnUp, -1);
  const btnDownCleanup = upDown(btnDown, +1);

  btnTop?.addEventListener("click", onTopClick);
  btnBottom?.addEventListener("click", onBottomClick);

  content.addEventListener("scroll", scheduleUpdateThumb, { passive: true });

  const onResize = () => scheduleUpdateThumb();
  window.addEventListener("resize", onResize);

  const ro = new ResizeObserver(scheduleUpdateThumb);
  ro.observe(content);
  ro.observe(track);

  const mo = new MutationObserver(scheduleUpdateThumb);
  mo.observe(content, { childList: true, subtree: true });

  // Initial sync after layout
  requestAnimationFrame(scheduleUpdateThumb);

  // ─── Destroy ─────────────────────────────────────────
  return function destroy() {
    stopScrolling();

    thumb.removeEventListener("mousedown", onThumbMouseDown);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);

    thumb.removeEventListener("touchstart", onThumbTouchStart);
    document.removeEventListener("touchmove", onTouchMove);
    document.removeEventListener("touchend", onTouchEnd);

    btnUpCleanup.forEach((fn) => fn());
    btnDownCleanup.forEach((fn) => fn());

    btnTop?.removeEventListener("click", onTopClick);
    btnBottom?.removeEventListener("click", onBottomClick);

    content.removeEventListener("scroll", scheduleUpdateThumb);
    window.removeEventListener("resize", onResize);

    ro.disconnect();
    mo.disconnect();
  };
}
